import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { getTranslatedName, getCurrentLang } from "@/lib/translation-utils";
import { getMeasurementFieldLabel } from "@shared/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthLite } from "@/context/AuthLite";
import { apiUrl } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { perfMarks } from "@/lib/perf-marks";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useRef, useState, useEffect } from "react";
import * as React from "react";
import { FilesTab, DealTimeline, PhotosTab } from "@/components/crm/DealExtras";
import { SuperfakturaDocumentsTab } from "@/components/crm/SuperfakturaDocumentsTab";
import { DealCompaniesSection } from "@/components/crm/DealCompaniesSection";

import StageProgress from "@/components/crm/StageProgress";
import EmailTab from "@/components/email/EmailTab";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronDown, ChevronsUpDown, Pencil, Check as CheckIcon, X, Plus, Trash2, FileText, UserCircle, Package, MoreHorizontal, Loader2 } from "lucide-react";
import { ClosingDatePickerContent } from "@/components/crm/ClosingDatePickerContent";
import { format } from "date-fns";
import { getDateFnsLocale } from "@/lib/date-fns-locale";
import { cn } from "@/lib/utils";
import {
  buildMeasurementMapsUrl,
  isPreciseMeasurementAddress,
  resolveDealGeo,
} from "@/lib/measurement-location";
import { dealClosingDayIso, closingDateDisplayClass } from "@/lib/crm-closing-date";
import { getDealInstallationDateFromRow } from "@/lib/crm-deal-stage";
import { InstallationDateDialog } from "@/components/crm/InstallationDateDialog";
import { isInstallationDateCaptureStageRow } from "../../functions/lib/deal-installation-date";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSuperfakturaDealAutoLink } from "@/hooks/use-superfaktura-deal-auto-link";
import Loading from "@/components/Loading";
import { ProductItemForm } from "@/components/measurement/ProductItemForm";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { notify } from "@/lib/notify";
import { postDealNoteWithOutbox } from "@/lib/post-deal-note-with-outbox";
import { gravatarUrl } from "@/lib/gravatar";
import { DealFabQuickActions } from "@/components/crm/DealFabQuickActions";
import { DealNotesTab } from "@/components/crm/DealNotesTab";
import { recordMove } from "@/lib/crm-deals-move";
import { getDebugRequestHeaders, getTrackedDealId, crmDebugDeal } from "@/lib/crm-debug";
import type { ProductType, DealProductItem, MeasurementData } from "@shared/api";

/** Egységes adatdoboz: Produkty / Súvisiaci kontakt / Spoločnosti (referencia: Deal termék kártya) */
const DEAL_DETAIL_DATA_CARD =
  "rounded-lg border border-border bg-card p-3 text-sm";

const MOBILE_DEAL_TABS = [
  "information",
  "notes",
  "superfaktura",
  "emails",
  "files",
  "photos",
  "timeline",
] as const;

const DESKTOP_DEAL_TABS = [
  "notes",
  "superfaktura",
  "emails",
  "files",
  "photos",
  "timeline",
] as const;

type DealTabValue = (typeof MOBILE_DEAL_TABS)[number];
const MOBILE_DEAL_TAB_MIN_HEIGHT_CLASS = "min-h-[calc(100dvh-15rem)]";

function getInitialsFromEmail(email: string): string {
  if (!email?.trim()) return "?";
  const [local, domain] = email.split("@");
  if (!local || !domain) return (local || email).slice(0, 2).toUpperCase() || "?";
  return (local[0]! + domain[0]!).toUpperCase();
}

function RelatedContact({
  contactId,
  edit,
  onContactChange,
  headerActions,
  onCloseEdit,
  onSaved,
  contactLinkDirty,
  onSaveContactLink,
}: {
  contactId?: string | null;
  edit?: boolean;
  onContactChange?: (newContactId: string | null) => void;
  /** Szerkesztés / mentés gombok — a kártya fejlécében jobbra (mint a terméknél) */
  headerActions?: React.ReactNode;
  /** Cancel / discard edit (restores previous contactId) */
  onCloseEdit?: () => void;
  /** Close edit after successful save (keep selected contactId) */
  onSaved?: () => void;
  /** True when picker selected a different contact than the deal currently has */
  contactLinkDirty?: boolean;
  /** Persist deal.contactId (without requiring contact field edits) */
  onSaveContactLink?: () => Promise<void>;
}) {
  const qc = useQueryClient();
  const { t } = useTranslation();

  const contacts = useQuery({
    queryKey: queryKeys.crmContacts,
    queryFn: async () => {
      const r = await fetch(apiUrl("/api/crm/contacts"), {
        credentials: "include",
      });
      const j = await r.json();
      return j.contacts as any[];
    },
  });
  
  // Fetch individual contact if not found in list (fallback)
  const contactQuery = useQuery({
    queryKey: queryKeys.crmContactById(contactId),
    queryFn: async () => {
      if (!contactId) return null;
      const r = await fetch(apiUrl(`/api/crm/contacts/${contactId}`), {
        credentials: "include",
      });
      if (!r.ok) return null;
      const j = await r.json();
      return j; // API returns contact directly, not wrapped in { contact: ... }
    },
    placeholderData: () => {
      const allContacts = qc.getQueryData<any[]>(queryKeys.crmContacts) || [];
      return allContacts.find((c) => c?.id === contactId) ?? null;
    },
    enabled: !!contactId, // Only fetch if contactId exists
  });
  const upd = useMutation({
    mutationFn: async (patch: {
      name?: string;
      email?: string | null;
      phone?: string | null;
      city?: string | null;
      street?: string | null;
    }) => {
      if (!contactId) throw new Error("no_contact");
      const r = await fetch(apiUrl(`/api/crm/contacts/${contactId}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(patch),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const msg =
          (body as any)?.userMessage ??
          (body as any)?.message ??
          (body as any)?.details?.message ??
          t("deal.contactUpdateError");
        throw new Error(msg);
      }
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.crmContacts });
      qc.invalidateQueries({ queryKey: queryKeys.crmContactById(contactId) });
    },
  });

  // CRITICAL: All hooks must be declared at the top level, before any conditional returns
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const [draft, setDraft] = React.useState<{
    name: string;
    email: string;
    phone: string;
    city: string;
    street: string;
  } | null>(null);
  const [saveState, setSaveState] = React.useState<"idle" | "saving" | "saved" | "error">("idle");
  const [lastSavedAt, setLastSavedAt] = React.useState<number | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const trimmedPickerSearch = searchValue.trim();
  const contactPickerSearch = useQuery({
    queryKey: ["crm-contacts-picker-search", trimmedPickerSearch],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("search", trimmedPickerSearch);
      params.set("page", "1");
      params.set("pageSize", "50");
      const r = await fetch(apiUrl(`/api/crm/contacts?${params.toString()}`), {
        credentials: "include",
      });
      if (!r.ok) return [] as any[];
      const j = await r.json();
      return (j.contacts || []) as any[];
    },
    enabled: !!edit && !!onContactChange && trimmedPickerSearch.length >= 2,
    staleTime: 30_000,
  });

  // Picker list: client filter on full directory + server search (never miss older contacts)
  const filteredContacts = React.useMemo(() => {
    const list = contacts.data || [];
    const byId = new Map<string, any>();
    const add = (c: any) => {
      if (c?.id) byId.set(c.id, c);
    };

    if (!trimmedPickerSearch) {
      // Keep combobox snappy when closed/open without query
      list.slice(0, 80).forEach(add);
      if (contactId) {
        const cur =
          list.find((c: any) => c.id === contactId) || contactQuery.data;
        if (cur) add(cur);
      }
      return [...byId.values()];
    }

    const searchLower = trimmedPickerSearch.toLowerCase();
    list
      .filter((contact: any) => {
        const name = (contact.name || "").toLowerCase();
        const email = (contact.email || "").toLowerCase();
        return name.includes(searchLower) || email.includes(searchLower);
      })
      .forEach(add);
    (contactPickerSearch.data || []).forEach(add);
    return [...byId.values()];
  }, [
    contacts.data,
    trimmedPickerSearch,
    contactPickerSearch.data,
    contactId,
    contactQuery.data,
  ]);

  const currentContact = React.useMemo(() => {
    if (!edit) return null;
    if (!contactId) return null;
    // Prefer full list; fall back to per-id fetch if cache not ready yet
    return (
      (contacts.data || []).find((x: any) => x.id === contactId) ??
      contactQuery.data ??
      null
    );
  }, [contacts.data, contactQuery.data, contactId, edit]);

  const contactLoading =
    !!edit &&
    !!contactId &&
    !currentContact &&
    (contactQuery.isLoading || contactQuery.isFetching);

  React.useEffect(() => {
    if (!edit) return;
    if (!currentContact) {
      setDraft(null);
      setSaveState("idle");
      setLastSavedAt(null);
      setSaveError(null);
      return;
    }
    setDraft({
      name: String(currentContact.name || ""),
      email: String(currentContact.email || ""),
      phone: String(currentContact.phone || ""),
      city: String(currentContact.city || ""),
      street: String(currentContact.street || ""),
    });
    setSaveState("idle");
    setLastSavedAt(null);
    setSaveError(null);
  }, [currentContact, edit, contactId]);

  const dirty = React.useMemo(() => {
    if (!draft || !currentContact) return false;
    const normalizeOpt = (v: string) => (v.trim() ? v.trim() : "");
    return (
      (draft.name ?? "") !== String(currentContact.name || "") ||
      normalizeOpt(draft.email) !== String(currentContact.email || "") ||
      normalizeOpt(draft.phone) !== String(currentContact.phone || "") ||
      normalizeOpt(draft.city) !== String(currentContact.city || "") ||
      normalizeOpt(draft.street) !== String(currentContact.street || "")
    );
  }, [draft, currentContact]);

  if (edit) {
    return (
      <div className={cn(DEAL_DETAIL_DATA_CARD, "space-y-2")}>
        {/* Same ✓/✕ as view mode — save deal contactId without editing contact fields */}
        {headerActions ? (
          <div className="flex items-center justify-end gap-1 -mt-0.5 mb-1">{headerActions}</div>
        ) : null}
        {onContactChange && (
          <div>
            <label className="block text-xs text-foreground/70 mb-1">
              {t("deal.relatedContact")}
            </label>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <button
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between h-10 rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 flex items-center"
                >
                  <span className="truncate">
                    {currentContact
                      ? (currentContact.name || currentContact.email || contactId)
                      : contactLoading
                        ? t("deal.loadingShort")
                        : t("deal.selectContact")}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder={t("deal.searchContacts")}
                    value={searchValue}
                    onValueChange={setSearchValue}
                  />
                  <CommandList>
                    <CommandEmpty>{t("deal.noResults")}</CommandEmpty>
                    <CommandGroup>
                      {filteredContacts.map((contact: any) => (
                        <CommandItem
                          key={contact.id}
                          value={`${contact.name || ""} ${contact.email || ""}`}
                          onSelect={() => {
                            console.log("[RelatedContact] Contact selected:", { oldValue: contactId, newValue: contact.id });
                            if (onContactChange) {
                              onContactChange(contact.id);
                            }
                            setOpen(false);
                            setSearchValue("");
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              contactId === contact.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {contact.name || contact.email || contact.id}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        )}
        {currentContact && (
          <>
            <div className="pt-2 border-t">
              <label className="block text-xs text-foreground/70 mb-1">
                Kontakt adatok
              </label>
            </div>
            <input
              value={draft?.name ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setDraft((d) => (d ? { ...d, name: v } : d));
                setSaveState("idle");
                setSaveError(null);
              }}
              className="w-full px-3 py-2 rounded-lg border bg-background disabled:opacity-60"
              placeholder={t("deal.name")}
              disabled={saveState === "saving"}
            />
            <input
              value={draft?.email ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setDraft((d) => (d ? { ...d, email: v } : d));
                setSaveState("idle");
                setSaveError(null);
              }}
              className="w-full px-3 py-2 rounded-lg border bg-background disabled:opacity-60"
              placeholder={t("deal.email")}
              disabled={saveState === "saving"}
            />
            <input
              value={draft?.phone ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setDraft((d) => (d ? { ...d, phone: v } : d));
                setSaveState("idle");
                setSaveError(null);
              }}
              className="w-full px-3 py-2 rounded-lg border bg-background disabled:opacity-60"
              placeholder={t("deal.phone")}
              disabled={saveState === "saving"}
            />
            <input
              value={draft?.city ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setDraft((d) => (d ? { ...d, city: v } : d));
                setSaveState("idle");
                setSaveError(null);
              }}
              className="w-full px-3 py-2 rounded-lg border bg-background disabled:opacity-60"
              placeholder={t("deal.city")}
              disabled={saveState === "saving"}
            />
            <input
              value={draft?.street ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setDraft((d) => (d ? { ...d, street: v } : d));
                setSaveState("idle");
                setSaveError(null);
              }}
              className="w-full px-3 py-2 rounded-lg border bg-background disabled:opacity-60"
              placeholder={t("deal.street")}
              disabled={saveState === "saving"}
            />

            <div className="pt-2 border-t flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    if (!contactId || !currentContact || !draft) return;
                    setSaveState("saving");
                    setSaveError(null);

                    try {
                      if (contactLinkDirty && onSaveContactLink) {
                        await onSaveContactLink();
                      }

                      const normalizeOpt = (v: string) => (v.trim() ? v.trim() : "");
                      const next: any = {};
                      if (draft.name !== String(currentContact.name || "")) next.name = draft.name;

                      const email = normalizeOpt(draft.email);
                      if (email !== String(currentContact.email || "")) next.email = email ? email : null;

                      const phone = normalizeOpt(draft.phone);
                      if (phone !== String(currentContact.phone || "")) next.phone = phone ? phone : null;

                      const city = normalizeOpt(draft.city);
                      if (city !== String(currentContact.city || "")) next.city = city ? city : null;

                      const street = normalizeOpt(draft.street);
                      if (street !== String(currentContact.street || "")) next.street = street ? street : null;

                      if (Object.keys(next).length > 0) {
                        await upd.mutateAsync(next);
                      } else if (!contactLinkDirty) {
                        setSaveState("idle");
                        return;
                      }

                      setSaveState("saved");
                      setLastSavedAt(Date.now());
                      onSaved?.();
                      window.setTimeout(() => {
                        setSaveState((s) => (s === "saved" ? "idle" : s));
                      }, 3500);
                    } catch (e) {
                      const msg = e instanceof Error ? e.message : String(e);
                      setSaveState("error");
                      setSaveError(msg || t("common.saveFailed"));
                    }
                  }}
                  disabled={(!dirty && !contactLinkDirty) || saveState === "saving"}
                  className="px-3 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 transition-colors"
                >
                  {saveState === "saving" ? t("common.saving") : t("common.save")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!currentContact) return;
                    setDraft({
                      name: String(currentContact.name || ""),
                      email: String(currentContact.email || ""),
                      phone: String(currentContact.phone || ""),
                      city: String(currentContact.city || ""),
                      street: String(currentContact.street || ""),
                    });
                    setSaveState("idle");
                    setLastSavedAt(null);
                    setSaveError(null);
                    onCloseEdit?.();
                  }}
                  disabled={saveState === "saving"}
                  className="px-3 py-2 rounded-md text-sm font-medium border border-border bg-background hover:bg-muted disabled:opacity-50 transition-colors"
                >
                  {t("common.cancel")}
                </button>
              </div>

              <div className="text-xs text-muted-foreground text-right min-w-0">
                {saveState === "saved" && (
                  <span className="text-green-600 dark:text-green-400">
                    {t("common.saved")}
                    {lastSavedAt ? ` ${new Date(lastSavedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
                  </span>
                )}
                {saveState === "error" && (
                  <span className="text-red-600 dark:text-red-400">
                    {t("common.saveFailed")}
                  </span>
                )}
                {dirty && saveState !== "saving" && saveState !== "saved" && (
                  <span className="truncate">{t("deal.unsavedChanges")}</span>
                )}
              </div>
            </div>

            {saveState === "error" && saveError ? (
              <div className="text-xs text-red-600 dark:text-red-400">
                {saveError}
              </div>
            ) : null}
          </>
        )}
        {contactLoading && (
          <div className="text-sm text-foreground/60 pt-2">
            {t("deal.loadingShort")}
          </div>
        )}
        {!currentContact && !contactLoading && (
          <div className="text-sm text-foreground/60 pt-2">
            {t("deal.selectContactHint")}
          </div>
        )}
      </div>
    );
  }

  if (!contactId) return <div className="text-sm text-foreground/60">{t("deal.dash")}</div>;
  
  // Try to find contact in the list first
  let c = (contacts.data || []).find((x: any) => x.id === contactId);
  
  // If not found in list, use the individually fetched contact
  if (!c && contactQuery.data) {
    c = contactQuery.data;
  }
  
  if (!c) {
    if (contactQuery.isLoading) {
      return <div className="text-sm text-foreground/60">{t("deal.loadingShort")}</div>;
    }
    return <div className="text-sm text-foreground/60">{t("deal.dash")}</div>;
  }

  const companyTrade = (c as { company?: string | null }).company?.trim();

  return (
    <div className={DEAL_DETAIL_DATA_CARD}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-foreground">{c.name}</div>
        </div>
        {headerActions ? <div className="flex items-center gap-1 shrink-0">{headerActions}</div> : null}
      </div>
      <div className="space-y-2">
        {c.email && (
          <div className="flex justify-between gap-3 items-start">
            <span className="text-muted-foreground shrink-0">{t("deal.email")}</span>
            <a
              className="text-right font-medium text-primary hover:underline break-all min-w-0"
              href={`mailto:${c.email}`}
            >
              {c.email}
            </a>
          </div>
        )}
        {c.phone && (
          <div className="flex justify-between gap-3 items-start">
            <span className="text-muted-foreground shrink-0">{t("deal.phone")}</span>
            <a className="text-right font-medium text-primary hover:underline break-all" href={`tel:${c.phone}`}>
              {c.phone}
            </a>
          </div>
        )}
        {c.city && (
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground shrink-0">{t("deal.city")}</span>
            <span className="text-right font-medium text-foreground">{c.city}</span>
          </div>
        )}
        {c.street && (
          <div className="flex justify-between gap-3 items-start">
            <span className="text-muted-foreground shrink-0">{t("deal.street")}</span>
            <span className="text-right font-medium text-foreground break-words min-w-0">{c.street}</span>
          </div>
        )}
        {companyTrade && (
          <div className="flex justify-between gap-3 items-start">
            <span className="text-muted-foreground shrink-0">{t("deal.companyLabel")}</span>
            <span className="text-right font-medium text-foreground break-words min-w-0">{companyTrade}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DealPage() {
  const { t, i18n } = useTranslation();
  const lang = getCurrentLang(i18n.language);
  const dateLocale = getDateFnsLocale(i18n.language);
  const productTypesLang = lang;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();
  useSuperfakturaDealAutoLink(id);
  const isMobile = useIsMobile();
  const routeMarkEndDone = useRef(false);
  const filesTabFileInputRef = useRef<HTMLInputElement | null>(null);

  if (!id) {
    return <div className="p-6 text-red-600">{t("deal.noId")}</div>;
  }

  const deal = useQuery({
    queryKey: ["deal", id],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${id}`), {
        credentials: "include",
        headers: getDebugRequestHeaders(),
      });
      if (!r.ok) throw new Error(t("deal.notFound"));
      const j = await r.json();
      return j;
    },
    enabled: !!id,
  });

  const notes = useQuery({
    queryKey: ["deal-notes", id],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${id}/notes`), {
        credentials: "include",
      });
      if (!r.ok) throw new Error(t("deal.notesError"));
      return (await r.json()).notes as any[];
    },
    enabled: !!id,
  });

  // Tab counts (shared cache with child components)
  const documentsQuery = useQuery({
    queryKey: ["deal-documents", id],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${id}/documents`), { credentials: "include" });
      if (!r.ok) return { documents: [] };
      const data = await r.json();
      return data as { documents: any[] };
    },
    enabled: !!id,
  });


  const updatedByEmail = deal.data?.updatedBy ?? (deal.data as any)?.updated_by;
  const { data: lastModifierAvatarMap } = useQuery({
    queryKey: ["user-avatar-map", updatedByEmail],
    queryFn: async () => {
      if (!updatedByEmail) return {};
      const res = await fetch(apiUrl(`/api/user/avatar-map?emails=${encodeURIComponent(updatedByEmail)}`), {
        credentials: "include",
      });
      const d = (await res.json()) as { map?: Record<string, string> };
      return d.map ?? {};
    },
    enabled: !!updatedByEmail,
  });
  const lastModifierPicture = updatedByEmail ? (lastModifierAvatarMap?.[updatedByEmail.toLowerCase()] ?? gravatarUrl(updatedByEmail, 32)) : null;

  const contactIdForEmails = deal.data?.contactId ?? (deal.data as any)?.contact_id;
  const threadsQuery = useQuery({
    queryKey: ["email-threads", contactIdForEmails, id, "", false],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (contactIdForEmails) params.set("contactId", contactIdForEmails);
      if (id) params.set("dealId", id);
      params.set("limit", "50");
      const res = await fetch(apiUrl(`/api/email/threads?${params.toString()}`), { credentials: "include" });
      if (!res.ok) return [];
      const data = await res.json();
      return (data.threads || []) as any[];
    },
    enabled: !!id,
  });
  const filesQuery = useQuery({
    queryKey: ["deal-files", id],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${id}/files`), { credentials: "include" });
      if (!r.ok) return [];
      const j = await r.json();
      return (j.files || []) as any[];
    },
    enabled: !!id,
  });
  const imageFoldersQuery = useQuery({
    queryKey: ["image-folders", { dealId: id }],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/images/folders?dealId=${id}`), { credentials: "include" });
      if (!r.ok) return [];
      const j = await r.json();
      return (j.folders || []) as any[];
    },
    enabled: !!id,
  });

  // Persist deal's pipeline so CRM shows same pipeline when navigating back
  useEffect(() => {
    const pid = (deal.data as any)?.pipelineId ?? (deal.data as any)?.pipeline_id;
    if (pid) {
      try {
        localStorage.setItem("crm_selected_subpipeline_id", pid);
      } catch {
        /* ignore */
      }
    }
  }, [deal.data]);

  useEffect(() => {
    if (deal.data && id && getTrackedDealId() === id) {
      crmDebugDeal("9.deal_detail_RECEIVED", deal.data as any, "Deal-page-query");
    }
  }, [deal.data, id]);

  const notesCount = (notes.data || []).length;
  const superfakturaCount = (documentsQuery.data?.documents || []).length;
  const emailsCount = (threadsQuery.data || []).length;
  const filesCount = (filesQuery.data || []).length;
  const historyCount = (deal.data?.history || []).length;
  const photosCount = (imageFoldersQuery.data || []).reduce((sum, f) => {
    const pc = f.photo_counts ?? f.photoCounts ?? { bemeres: 0, munkaink: 0, marketing: 0 };
    return sum + (pc.bemeres || 0) + (pc.munkaink || 0) + (pc.marketing || 0);
  }, 0);

  const contacts = useQuery({
    queryKey: queryKeys.crmContacts,
    queryFn: async () => {
      const r = await fetch(apiUrl("/api/crm/contacts"), {
        credentials: "include",
      });
      const j = await r.json();
      return j.contacts as any[];
    },
  });

  // Per-id fetch: reliable while full contacts cache loads / for email fields
  const dealRelatedContactId = deal.data?.contactId ?? (deal.data as any)?.contact_id ?? null;
  const contactByIdQuery = useQuery({
    queryKey: queryKeys.crmContactById(dealRelatedContactId),
    queryFn: async () => {
      if (!dealRelatedContactId) return null;
      const r = await fetch(apiUrl(`/api/crm/contacts/${dealRelatedContactId}`), {
        credentials: "include",
      });
      if (!r.ok) return null;
      return r.json();
    },
    placeholderData: () => {
      const allContacts = qc.getQueryData<any[]>(queryKeys.crmContacts) || [];
      return allContacts.find((c) => c?.id === dealRelatedContactId) ?? undefined;
    },
    enabled: !!dealRelatedContactId,
  });

  const board = useQuery({
    queryKey: queryKeys.crmBoard,
    queryFn: async () => {
      const r = await fetch(apiUrl("/api/crm/board"), {
        credentials: "include",
      });
      const j = await r.json();
      return j as { stages: any[]; deals: any[]; pipelines: any[] };
    },
  });

  const siblingId = deal.data?.siblingDealId ?? deal.data?.sibling_deal_id;
  const siblingDealQuery = useQuery({
    queryKey: ["deal", siblingId],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${siblingId}`), { credentials: "include" });
      if (!r.ok) throw new Error(t("deal.siblingLoadFailed"));
      const j = await r.json();
      return j;
    },
    enabled: !!siblingId,
  });

  const measurementProductsForSplit = deal.data?.measurementData?.products ?? deal.data?.measurement_data?.products ?? [];
  const productTypesQuery = useQuery({
    queryKey: queryKeys.crmProductTypes(productTypesLang),
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/product-types?lang=${productTypesLang}`), { credentials: "include" });
      if (!r.ok) return [];
      const j = await r.json();
      const raw = (j.productTypes || []) as Array<{ id: string; name: string; name_hu?: string | null; name_sk?: string | null; name_en?: string | null; pipeline_category?: string; pipelineCategory?: string; measurement_fields?: any[]; measurementFields?: any[] }>;
      return raw.map((row) => ({
        id: row.id,
        name: row.name,
        name_hu: row.name_hu ?? undefined,
        name_sk: row.name_sk ?? undefined,
        name_en: row.name_en ?? undefined,
        pipelineCategory: row.pipelineCategory ?? row.pipeline_category ?? "garazskapuk",
        measurementFields: row.measurementFields ?? row.measurement_fields ?? [],
      })) as ProductType[];
    },
    enabled: !!id,
  });
  const productTypes = productTypesQuery.data || [];

  /** Megkeresi a terméktípust ID vagy bármely nyelvi név alapján (egységesítés Deal termékek ↔ Premenné Typy produktov). */
  function findProductTypeForItem(
    list: ProductType[],
    item: { productTypeId?: string; productType?: string }
  ): ProductType | undefined {
    if (!item) return undefined;
    if (item.productTypeId) {
      const byId = list.find((t) => t.id === item.productTypeId);
      if (byId) return byId;
    }
    const search = (item.productType ?? "").trim();
    if (!search) return undefined;
    return list.find(
      (t) =>
        t.name === search ||
        t.name_hu === search ||
        t.name_sk === search ||
        t.name_en === search
    );
  }

  const typeToCategory = new Map(productTypes.map((t) => [t.name, t.pipelineCategory ?? "garazskapuk"]));
  const hasGarazskapuk = measurementProductsForSplit.some((p: any) => {
    const pt = findProductTypeForItem(productTypes, p);
    return !pt || typeToCategory.get(pt.name) !== "klimak";
  });
  const hasKlimak = measurementProductsForSplit.some((p: any) => findProductTypeForItem(productTypes, p)?.pipelineCategory === "klimak");
  const hasBothCategories = hasGarazskapuk && hasKlimak;

  const createSiblingMutation = useMutation({
    mutationFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${id}/create-sibling`), {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err?.message || t("deal.splitFailed"));
      }
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal", id] });
      qc.invalidateQueries({ queryKey: queryKeys.crmBoard });
    },
  });

  const save = useMutation({
    mutationFn: async (patch: any) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${id}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(patch),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const msg =
          (body as any)?.details?.message ??
          (body as any)?.userMessage ??
          (body as any)?.message ??
          t("deal.saveFailed");
        throw new Error(msg);
      }
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal", id] }),
  });

  const [deleteDealDialogOpen, setDeleteDealDialogOpen] = useState(false);
  const [pendingWonStageId, setPendingWonStageId] = useState<string | null>(null);
  const [installationConfirming, setInstallationConfirming] = useState(false);

  const move = useMutation({
    mutationFn: async ({ stageId, installationDate }: { stageId: string; installationDate?: string }) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${id}/stage?_=${Date.now()}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          stageId,
          ...(installationDate ? { installationDate } : {}),
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error((body as any)?.details?.message ?? (body as any)?.message ?? t("deal.moveFailed"));
      }
      return r.json();
    },
    onSuccess: (data) => {
      const pipelineId = (data as any)?.pipelineId ?? (data as any)?.pipeline_id;
      const stageId = (data as any)?.stageId ?? (data as any)?.stage_id;
      recordMove(id, stageId, pipelineId);
      qc.invalidateQueries({ queryKey: ["deal", id] });
      qc.invalidateQueries({ queryKey: queryKeys.crmBoard });
    },
  });

  const requestStageMove = (stageId: string) => {
    const targetStage = (board.data?.stages ?? []).find((s: any) => s.id === stageId);
    const dealRow = deal.data as unknown as Record<string, unknown> | undefined;
    const currentStageId = dealRow?.stageId ?? dealRow?.stage_id;
    if (
      targetStage &&
      isInstallationDateCaptureStageRow(targetStage as unknown as Record<string, unknown>) &&
      stageId !== currentStageId &&
      !getDealInstallationDateFromRow(dealRow)
    ) {
      setPendingWonStageId(stageId);
      return;
    }
    move.mutate({ stageId });
  };

  const changePipeline = useMutation({
    mutationFn: async (pipelineId: string) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${id}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ pipelineId }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error((body as any)?.details?.message ?? (body as any)?.message ?? t("deal.pipelineChangeFailed"));
      }
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal", id] });
      qc.invalidateQueries({ queryKey: queryKeys.crmBoard });
    },
  });

  const deleteDealMutation = useMutation({
    mutationFn: async () => {
      const r = await fetch(apiUrl("/api/crm/deals/batch"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ operation: "delete", dealIds: [id] }),
      });
      const j = (await r.json().catch(() => ({}))) as {
        message?: string;
        error?: string;
        failed?: number;
        errors?: string[];
      };
      if (!r.ok) {
        throw new Error(j.message || j.error || t("deal.deleteDealFailed"));
      }
      if (typeof j.failed === "number" && j.failed > 0) {
        throw new Error(j.errors?.[0] || t("deal.deleteDealFailed"));
      }
      return j;
    },
    onSuccess: () => {
      setDeleteDealDialogOpen(false);
      qc.invalidateQueries({ queryKey: queryKeys.crmBoard });
      qc.invalidateQueries({ queryKey: queryKeys.crmDeals });
      qc.invalidateQueries({ queryKey: ["deal", id] });
      qc.invalidateQueries({ queryKey: ["deal-notes", id] });
      qc.invalidateQueries({ queryKey: ["deal-documents", id] });
      notify.success(t("deal.deleteDealSuccess"));
      navigate("/crm");
    },
    onError: (err) => {
      notify.error(
        t("deal.deleteDealFailed"),
        err instanceof Error ? err.message : String(err),
      );
    },
  });

  const addNote = useMutation({
    mutationFn: async (body: string) => {
      const ok = await postDealNoteWithOutbox(id!, body);
      if (!ok) {
        notify.info(t("uploadOutbox.noteQueued"), t("uploadOutbox.noteQueuedDesc"));
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-notes", id] });
      qc.invalidateQueries({ queryKey: ["deal", id] });
    },
  });

  const [editTitle, setEditTitle] = useState(false);
  const [titleValue, setTitleValue] = useState<string>("");
  const [titleValueTemp, setTitleValueTemp] = useState<string>("");
  const [editTags, setEditTags] = useState(false);
  const [editContact, setEditContact] = useState(false);
  const [contactIdTemp, setContactIdTemp] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [editClosingDate, setEditClosingDate] = useState(false);
  const [closingDateValue, setClosingDateValue] = useState<string>("");
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [editingProductDraft, setEditingProductDraft] = useState<DealProductItem | null>(null);
  const [addProductTypeName, setAddProductTypeName] = useState<string>("");
  const [activeDealTab, setActiveDealTab] = useState<DealTabValue>(isMobile ? "information" : "notes");
  const [tabSwipeDirection, setTabSwipeDirection] = useState<-1 | 0 | 1>(0);

  const tabParam = searchParams.get("tab");
  useEffect(() => {
    if (!id || !tabParam) return;
    const order = isMobile ? MOBILE_DEAL_TABS : DESKTOP_DEAL_TABS;
    if ((order as readonly string[]).includes(tabParam)) {
      setActiveDealTab(tabParam as DealTabValue);
      setTabSwipeDirection(0);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        return next;
      },
      { replace: true },
    );
  }, [id, tabParam, isMobile, setSearchParams]);

  const tabTouchStartRef = useRef<{ x: number; y: number } | null>(null);
  const tabTouchScrollableRef = useRef<HTMLElement | null>(null);
  const tabTouchStartedInTableRef = useRef(false);

  /** Általános tab-swipe: kevésbé érzékeny (véletlen váltások ellen). */
  const SWIPE_MIN_DELTA_X = 90;
  const SWIPE_MIN_HORIZONTAL_RATIO = 1.45;
  /** Táblázat közelében / felett: sokkal szigorúbb (táblázat görgetés vs. oldalváltás). */
  const SWIPE_TABLE_MIN_DELTA_X = 160;
  const SWIPE_TABLE_MIN_HORIZONTAL_RATIO = 2.35;

  const setDealTabWithDirection = (nextTab: DealTabValue) => {
    const currentTabOrder = isMobile ? MOBILE_DEAL_TABS : DESKTOP_DEAL_TABS;
    const prevIndex = currentTabOrder.findIndex((tab) => tab === activeDealTab);
    const nextIndex = currentTabOrder.findIndex((tab) => tab === nextTab);
    if (prevIndex !== -1 && nextIndex !== -1 && prevIndex !== nextIndex) {
      setTabSwipeDirection(nextIndex > prevIndex ? 1 : -1);
    } else {
      setTabSwipeDirection(0);
    }
    setActiveDealTab(nextTab);
  };

  const isInTableRegion = (target: HTMLElement, boundary: HTMLElement): boolean => {
    const el = target.closest("table, [role='grid'], [data-deal-table-region]");
    if (!el) return false;
    return boundary.contains(el);
  };

  const findHorizontalScrollableAncestor = (
    target: HTMLElement,
    boundary: HTMLElement
  ): HTMLElement | null => {
    let node: HTMLElement | null = target;
    while (node && node !== boundary) {
      const style = window.getComputedStyle(node);
      const overflowX = style.overflowX;
      const canScrollX = (overflowX === "auto" || overflowX === "scroll") && node.scrollWidth > node.clientWidth + 2;
      if (canScrollX) return node;
      node = node.parentElement;
    }
    return null;
  };

  const handleDealTabTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    const target = e.target as HTMLElement;
    if (target.closest("input, textarea, select, [role='tablist']")) {
      tabTouchStartRef.current = null;
      tabTouchScrollableRef.current = null;
      tabTouchStartedInTableRef.current = false;
      return;
    }
    tabTouchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    tabTouchScrollableRef.current = findHorizontalScrollableAncestor(target, e.currentTarget);
    tabTouchStartedInTableRef.current = isInTableRegion(target, e.currentTarget);
  };

  const handleDealTabTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile || !tabTouchStartRef.current) return;
    // no-op: tab váltást touchEnd-ben döntjük el a végső delta alapján
    // (így mobilon megbízhatóbban elkapja a gyors swipe-okat is)
  };

  const handleDealTabTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile || !tabTouchStartRef.current) return;
    const start = tabTouchStartRef.current;
    tabTouchStartRef.current = null;
    const deltaX = e.changedTouches[0].clientX - start.x;
    const deltaY = e.changedTouches[0].clientY - start.y;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    const inTable = tabTouchStartedInTableRef.current;
    tabTouchStartedInTableRef.current = false;

    const minDx = inTable ? SWIPE_TABLE_MIN_DELTA_X : SWIPE_MIN_DELTA_X;
    const minRatio = inTable ? SWIPE_TABLE_MIN_HORIZONTAL_RATIO : SWIPE_MIN_HORIZONTAL_RATIO;
    if (absX < minDx || absX < absY * minRatio) {
      tabTouchScrollableRef.current = null;
      return;
    }

    const scrollable = tabTouchScrollableRef.current;
    tabTouchScrollableRef.current = null;
    if (scrollable) {
      const maxScrollLeft = scrollable.scrollWidth - scrollable.clientWidth;
      const canScrollLeft = scrollable.scrollLeft > 1;
      const canScrollRight = scrollable.scrollLeft < maxScrollLeft - 1;
      if ((deltaX > 0 && canScrollLeft) || (deltaX < 0 && canScrollRight)) {
        return;
      }
    }

    const currentIndex = MOBILE_DEAL_TABS.indexOf(activeDealTab);
    if (currentIndex === -1) return;
    if (deltaX < 0) {
      const nextIndex = Math.min(currentIndex + 1, MOBILE_DEAL_TABS.length - 1);
      if (nextIndex !== currentIndex) setDealTabWithDirection(MOBILE_DEAL_TABS[nextIndex]);
      return;
    }
    const prevIndex = Math.max(currentIndex - 1, 0);
    if (prevIndex !== currentIndex) setDealTabWithDirection(MOBILE_DEAL_TABS[prevIndex]);
  };

  useEffect(() => {
    if (deal.data) {
      if (!routeMarkEndDone.current) {
        perfMarks.crmRouteEnd();
        routeMarkEndDone.current = true;
      }
      setTitleValue(deal.data.title || "");
      setTitleValueTemp(deal.data.title || "");
      setContactIdTemp(deal.data.contactId || null);
      const closingDateStr = deal.data.closingDate ? deal.data.closingDate.split("T")[0] : "";
      setClosingDateValue(closingDateStr);
      // Initialize field values
      setFieldValues({
        width: deal.data.width || "",
        height: deal.data.height || "",
        color: deal.data.color || "",
        surface: deal.data.surface || "",
        emboss: deal.data.emboss || "",
        closingDate: closingDateStr,
      });
    }
  }, [deal.data]);

  useEffect(() => {
    setActiveDealTab((prev) => {
      if (!isMobile && prev === "information") return "notes";
      return prev;
    });
  }, [isMobile]);

  if (deal.isLoading || board.isLoading)
    return <Loading message={t("deal.loading")} />;
  if (deal.isError)
    return <div className="p-6 text-red-600">{t("deal.error")}</div>;
  if (!deal.data)
    return <Loading message={t("deal.loading")} />;

  const d = deal.data;
  const measurementData = d?.measurementData ?? d?.measurement_data;
  const measurementProducts = measurementData?.products ?? [];
  const measurementGates = measurementData?.gates || [];
  const dealContact = d?.contactId
    ? (contacts.data || []).find((c: any) => c.id === d.contactId) ??
      (contactByIdQuery.data?.id === d.contactId ? contactByIdQuery.data : null)
    : null;
  // Never fall back to ownerEmail — that is the deal owner (e.g. info@), not the customer
  const dealEmail = (dealContact?.email && String(dealContact.email).trim()) || null;
  const dealContactName = (dealContact?.name && String(dealContact.name).trim()) || undefined;

  // Get pipeline information
  const dealPipelineId = d?.pipelineId || d?.pipeline_id;
  const dealPipeline = dealPipelineId ? (board.data?.pipelines || []).find((p: any) => p.id === dealPipelineId) : null;
  // Only treat as sub-pipeline when the pipeline has a parent (is actually a sub-pipeline)
  const isActuallySubPipeline = dealPipeline && (dealPipeline.parentPipelineId ?? dealPipeline.parent_pipeline_id);
  const subPipeline = isActuallySubPipeline ? dealPipeline : null;
  const parentPipelineId = subPipeline
    ? (subPipeline.parentPipelineId ?? subPipeline.parent_pipeline_id)
    : (dealPipeline && !isActuallySubPipeline ? dealPipeline.id : null);
  const teamPipeline = parentPipelineId ? (board.data?.pipelines || []).find((p: any) => p.id === parentPipelineId) : (dealPipeline && !isActuallySubPipeline ? dealPipeline : null);

  // Get available subpipelines for the same parent pipeline
  const availableSubPipelines = parentPipelineId
    ? (board.data?.pipelines || []).filter(
        (p: any) => (p.parentPipelineId || p.parent_pipeline_id) === parentPipelineId
      )
    : [];
  // When deal has no sub-pipeline, show all sub-pipelines so user can assign one
  const availableSubPipelinesForSelect =
    availableSubPipelines.length > 0
      ? availableSubPipelines
      : (board.data?.pipelines || []).filter((p: any) => p.parentPipelineId || p.parent_pipeline_id);
  // Parent (team) pipelines for mobile dropdown
  const parentPipelines = (board.data?.pipelines || []).filter(
    (p: any) => !(p.parentPipelineId || p.parent_pipeline_id)
  );
  // Sub-pipelines per parent (for switching team: pick first sub of selected parent)
  const subPipelinesByParent = new Map<string, any[]>();
  (board.data?.pipelines || []).forEach((p: any) => {
    const pid = p.parentPipelineId || p.parent_pipeline_id;
    if (pid) {
      const list = subPipelinesByParent.get(pid) || [];
      list.push(p);
      subPipelinesByParent.set(pid, list);
    }
  });
  // Stages of current deal pipeline (for mobile stage dropdown)
  const mobileDealStages = (() => {
    const pid = d?.pipelineId || d?.pipeline_id;
    if (!pid || !board.data?.stages) return [];
    const list = (board.data.stages as any[]).filter(
      (s: any) => s.pipelineId === pid || s.pipeline_id === pid
    );
    return list.sort((a: any, b: any) => (a.order ?? a.order_index ?? a.orderIndex ?? 0) - (b.order ?? b.order_index ?? b.orderIndex ?? 0));
  })();

  // Get current stage name
  const currentStage = board.data?.stages?.find(
    (s: any) => s.id === d?.stageId || s.id === d?.stage_id
  );
  const currentStageName = currentStage ? getTranslatedName(currentStage, lang) || "—" : "—";

  return (
    <div
      className={cn("px-4 py-6", isMobile && "overflow-x-hidden max-w-full px-3 py-4")}
      onTouchStart={handleDealTabTouchStart}
      onTouchMove={handleDealTabTouchMove}
      onTouchEnd={handleDealTabTouchEnd}
      style={isMobile ? { touchAction: "pan-y" } : undefined}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0 flex-1 group">
          {editTitle ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <input
                value={titleValueTemp}
                onChange={(e) => setTitleValueTemp(e.target.value)}
                autoFocus
                className="text-2xl font-bold bg-background border rounded px-2 py-1 flex-1 min-w-0"
              />
              <button
                onClick={() => {
                  save.mutate(
                    { title: titleValueTemp },
                    {
                      onSuccess: () => {
                        setEditTitle(false);
                        setTitleValue(titleValueTemp);
                      },
                      onError: (error: any) => {
                        const errorMessage = error instanceof Error ? error.message : String(error);
                        alert(t("deal.errorPrefix") + errorMessage);
                      },
                    }
                  );
                }}
                className="p-1.5 hover:bg-green-50 dark:hover:bg-green-950/30 text-green-600 dark:text-green-400 rounded"
                aria-label={t("common.save")}
                type="button"
                disabled={save.isPending}
              >
                <CheckIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => {
                  setEditTitle(false);
                  setTitleValueTemp(titleValue);
                }}
                className="p-1.5 hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600 dark:text-red-400 rounded"
                aria-label={t("common.cancel")}
                type="button"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 min-w-0 flex-1 group">
              <h1 className="text-2xl font-bold min-w-0">{titleValue || d?.title || "—"}</h1>
              <button
                onClick={() => {
                  setEditTitle(true);
                  setTitleValueTemp(titleValue || d?.title || "");
                }}
                className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-muted rounded transition-opacity"
                aria-label={t("deal.edit")}
                type="button"
              >
                <Pencil className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
        {!editTitle && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                aria-label={t("deal.moreActionsAriaLabel")}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={() => setDeleteDealDialogOpen(true)}
              >
                {t("deal.deleteDealMenuItem")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Deal metadata: Owner, Pipeline, Sub-Pipeline, Closing Date (Desktop only) */}
      {!isMobile && (
        <>
          <div className="mb-4 space-y-2 text-sm text-foreground/70">
            {/* Owner */}
            {d?.ownerEmail && (
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
                <span>{d.ownerEmail}</span>
              </div>
            )}

            {/* Pipeline information */}
            <div className="flex items-center gap-4 flex-wrap">
              {teamPipeline && (
                <div>
                  <span className="font-medium">{t("deal.teamPipeline")}</span> {getTranslatedName(teamPipeline, lang)}
                </div>
              )}
              {(subPipeline || availableSubPipelinesForSelect.length > 0) && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">{t("deal.subPipeline")}</span>
                  {subPipeline ? (
                    availableSubPipelinesForSelect.length > 1 ? (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 font-normal text-inherit hover:bg-muted/50 rounded px-0.5 py-0.5 -mx-0.5"
                            aria-label={t("deal.edit")}
                          >
                            <span>{getTranslatedName(subPipeline, lang)}</span>
                            <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-0" align="start">
                          <Command>
                            <CommandInput placeholder={t("deal.search")} />
                            <CommandList>
                              <CommandEmpty>{t("deal.noResults")}</CommandEmpty>
                              <CommandGroup>
                                {availableSubPipelinesForSelect.map((p: any) => (
                                  <CommandItem
                                    key={p.id}
                                    value={getTranslatedName(p, lang)}
                                    onSelect={() => changePipeline.mutate(p.id)}
                                  >
                                    <Check className={cn("mr-2 h-4 w-4 flex-shrink-0", subPipeline?.id === p.id ? "opacity-100" : "opacity-0")} />
                                    <span className="truncate">{getTranslatedName(p, lang)}</span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <span>{getTranslatedName(subPipeline, lang)}</span>
                    )
                  ) : (
                    availableSubPipelinesForSelect.length > 0 ? (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 font-normal text-inherit hover:bg-muted/50 rounded px-0.5 py-0.5 -mx-0.5"
                            aria-label={t("deal.edit")}
                          >
                            <span>—</span>
                            <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-0" align="start">
                          <Command>
                            <CommandInput placeholder={t("deal.search")} />
                            <CommandList>
                              <CommandEmpty>{t("deal.noResults")}</CommandEmpty>
                              <CommandGroup>
                                {availableSubPipelinesForSelect.map((p: any) => (
                                  <CommandItem
                                    key={p.id}
                                    value={getTranslatedName(p, lang)}
                                    onSelect={() => changePipeline.mutate(p.id)}
                                  >
                                    <Check className="mr-2 h-4 w-4 flex-shrink-0 opacity-0" />
                                    <span className="truncate">{getTranslatedName(p, lang)}</span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <span>—</span>
                    )
                  )}
                </div>
              )}
              
              {/* Closing Date - editable with calendar popover */}
              <div className={cn("flex items-center gap-2", isMobile && "flex-col items-start gap-2")}>
                <span className={cn("font-medium", isMobile && "text-sm")}>{t("deal.closingDate")}</span>
                <Popover
                  open={editClosingDate}
                  onOpenChange={(open) => {
                    setEditClosingDate(open);
                    if (open) {
                      setClosingDateValue(
                        d?.closingDate ? d.closingDate.split("T")[0] : format(new Date(), "yyyy-MM-dd"),
                      );
                    }
                  }}
                >
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-2 group hover:bg-muted/50 rounded px-1 py-0.5 -mx-1"
                      aria-label={t("deal.edit")}
                    >
                      <span
                        className={cn(
                          d.closingDate ? closingDateDisplayClass(dealClosingDayIso(d)) : "text-muted-foreground",
                        )}
                      >
                        {d.closingDate
                          ? new Date(d.closingDate).toLocaleDateString("hu-HU", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })
                          : "—"}
                      </span>
                      <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 rounded-lg shadow-lg border" align="center" side="bottom" sideOffset={6}>
                    <ClosingDatePickerContent
                      value={closingDateValue}
                      onChange={setClosingDateValue}
                      onCancel={() => {
                        setEditClosingDate(false);
                        setClosingDateValue(d?.closingDate ? d.closingDate.split("T")[0] : "");
                      }}
                      onSave={() => {
                        save.mutate(
                          { closingDate: closingDateValue || null },
                          {
                            onSuccess: () => {
                              setEditClosingDate(false);
                              setFieldValues((prev) => ({ ...prev, closingDate: closingDateValue }));
                            },
                            onError: (error: any) => {
                              const errorMessage = error instanceof Error ? error.message : String(error);
                              alert(t("deal.errorPrefix") + errorMessage);
                            },
                          }
                        );
                      }}
                      saving={save.isPending}
                      locale={dateLocale}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          <div className="mb-6 -mx-4 px-4">
            <StageProgress
              stages={
                // Filter stages to only show those from the deal's sub-pipeline
                (() => {
                  const dealPipelineId = d?.pipelineId || d?.pipeline_id;
                  if (!dealPipelineId || !board.data) {
                    return board.data?.stages || [];
                  }
                  // Filter stages that belong to this sub-pipeline and sort by order
                  const filteredStages = (board.data?.stages || []).filter(
                    (s: any) => s.pipelineId === dealPipelineId || s.pipeline_id === dealPipelineId
                  );
                  // Sort by order (handle both order and order_index field names)
                  return filteredStages.sort((a: any, b: any) => {
                    const orderA = a.order ?? a.order_index ?? a.orderIndex ?? 0;
                    const orderB = b.order ?? b.order_index ?? b.orderIndex ?? 0;
                    return orderA - orderB;
                  });
                })()
              }
              current={d?.stageId}
              onSelect={(sid) => requestStageMove(sid)}
            />
          </div>

          {/* Ketté bontott deal: testvér link + státusz */}
          {siblingId && (
            <div className="mb-4 px-4 py-3 rounded-lg border bg-muted/40">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("deal.splitDealTitle")}</span>
              {siblingDealQuery.data ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Link
                    to={`/crm/deals/${siblingId}`}
                    className="text-primary font-medium hover:underline"
                  >
                    {(siblingDealQuery.data as any).title || t("deal.siblingDeal")}
                  </Link>
                  {board.data?.stages && (
                    <span className="text-sm text-muted-foreground">
                      — {(board.data.stages as any[]).find((s: any) => s.id === ((siblingDealQuery.data as any).stageId ?? (siblingDealQuery.data as any).stage_id))?.name ?? "—"}
                    </span>
                  )}
                </div>
              ) : siblingDealQuery.isLoading ? (
                <p className="mt-2 text-sm text-muted-foreground">{t("deal.loadingShort")}</p>
              ) : null}
            </div>
          )}

          {/* Ketté bontás gomb ha van kapu + klíma és még nincs testvér */}
          {!siblingId && hasBothCategories && (
            <div className="mb-4 px-4">
              <button
                type="button"
                onClick={() => {
                  if (confirm(t("deal.splitConfirm"))) {
                    createSiblingMutation.mutate(undefined, {
                      onError: (e) => alert(e.message),
                    });
                  }
                }}
                disabled={createSiblingMutation.isPending}
                className="px-3 py-2 rounded-md border bg-background text-sm font-medium hover:bg-muted"
              >
                {createSiblingMutation.isPending ? t("deal.splitting") : t("deal.splitButton")}
              </button>
            </div>
          )}
        </>
      )}

      <div className={cn("grid grid-cols-1 gap-6", !isMobile && "md:grid-cols-[360px,1fr]")}>
        {!isMobile && (
          <div className="space-y-4">
            {/* Combined card for Related Contact, Description, and Other Info */}
            <div className="p-4 border rounded-lg bg-card space-y-4">
            {/* Related Contact Section */}
            <div>
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-1.5">
                <UserCircle className="h-4 w-4" />
                {t("deal.relatedContactHeading")}
              </h3>
              <RelatedContact
                contactId={editContact ? contactIdTemp : d.contactId}
                edit={editContact}
                onContactChange={(newContactId) => {
                  setContactIdTemp(newContactId);
                }}
                contactLinkDirty={
                  !!editContact &&
                  !!contactIdTemp &&
                  contactIdTemp !== (d.contactId || (d as any).contact_id || null)
                }
                onSaveContactLink={async () => {
                  if (!contactIdTemp) throw new Error(t("deal.contactRequired"));
                  await save.mutateAsync({ contactId: contactIdTemp });
                  qc.invalidateQueries({ queryKey: queryKeys.crmContacts });
                }}
                onSaved={() => setEditContact(false)}
                onCloseEdit={() => {
                  setEditContact(false);
                  setContactIdTemp(d.contactId || null);
                }}
                headerActions={
                  !editContact ? (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditContact(true);
                        setContactIdTemp(d.contactId || (d as any).contact_id || null);
                      }}
                      className="p-1.5 rounded-md border border-border bg-background hover:bg-muted text-muted-foreground hover:text-foreground"
                      aria-label={t("deal.edit")}
                      type="button"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!contactIdTemp) {
                            alert(t("deal.contactRequired"));
                            return;
                          }
                          save.mutate(
                            { contactId: contactIdTemp },
                            {
                              onSuccess: () => {
                                setEditContact(false);
                                qc.invalidateQueries({ queryKey: queryKeys.crmContacts });
                              },
                              onError: (error: any) => {
                                const errorMessage = error instanceof Error ? error.message : String(error);
                                alert(t("deal.contactSaveError") + errorMessage);
                              },
                            }
                          );
                        }}
                        className="p-1.5 rounded-md border border-border bg-background hover:bg-muted text-green-600 dark:text-green-400"
                        aria-label={t("common.save")}
                        type="button"
                        disabled={save.isPending}
                      >
                        <CheckIcon className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setEditContact(false);
                          setContactIdTemp(d.contactId || null);
                        }}
                        className="p-1.5 rounded-md border border-border bg-background hover:bg-muted text-red-600 dark:text-red-400"
                        aria-label={t("common.cancel")}
                        type="button"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )
                }
              />
            </div>

            {/* Cégek Section */}
            <DealCompaniesSection dealId={id!} />

            {/* Termékek Section */}
            <div className="border-t pt-4 space-y-2">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-1.5">
                <Package className="h-4 w-4" />
                {t("deal.productsHeading")}
              </h3>
              {/* Termék hozzáadása – teljes szélesség asztali nézetben */}
              {productTypes.length > 0 && (
                <div className="flex items-center gap-2 flex-nowrap mb-3 w-full max-w-full">
                  <select
                    value={addProductTypeName}
                    onChange={(e) => setAddProductTypeName(e.target.value)}
                    className="h-9 flex-1 min-w-0 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-0 focus:border-border focus-visible:outline-none focus-visible:ring-0 focus-visible:border-border"
                  >
                    <option value="">{t("deal.addProduct")}</option>
                    {productTypes.map((pt) => (
                      <option key={pt.id} value={pt.name}>{pt.name}</option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      if (!addProductTypeName) return;
                      const pt = productTypes.find((t) => t.name === addProductTypeName);
                      const sameType = measurementProducts.filter((p: any) => p.productType === addProductTypeName);
                      const nextIndex = sameType.length > 0 ? Math.max(...sameType.map((p: any) => p.index || 0)) + 1 : 1;
                      const newItem: DealProductItem = {
                        id: crypto.randomUUID(),
                        productType: addProductTypeName,
                        productTypeId: pt?.id,
                        index: nextIndex,
                        widths: [],
                        heights: [],
                        notes: "",
                        surface: "",
                        color: "",
                        prelis: "",
                      };
                      const nextProducts = [...measurementProducts, newItem];
                      const nextData: MeasurementData = { products: nextProducts, gates: measurementGates };
                      save.mutate({ measurementData: nextData });
                      setAddProductTypeName("");
                    }}
                    disabled={!addProductTypeName || save.isPending}
                    className="h-9 w-9 min-w-9 shrink-0 p-0"
                    title={t("deal.addProductButton")}
                    aria-label={t("deal.addProductButton")}
                  >
                    <Plus className="h-5 w-5" />
                  </Button>
                </div>
              )}
              {/* Termék alapú megjelenítés: Garázskapu 1, Tolómotor automatizáció 1, stb. */}
              {measurementProducts.length > 0 ? (
                <div className="space-y-4">
                  {measurementProducts.map((p: any, idx: number) => (
                    <div key={p.id || idx} className={cn(DEAL_DETAIL_DATA_CARD, "space-y-0")}>
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="min-w-0">
                          <h4 className="text-sm font-semibold text-foreground">
                            {findProductTypeForItem(productTypes, p)?.name ?? p.productType} {p.index}
                          </h4>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            {(() => {
                              const wh =
                                p.widths?.length && p.heights?.length
                                  ? `${p.widths.join(", ")} × ${p.heights.join(", ")} mm`
                                  : p.widths?.length
                                    ? `${p.widths.join(", ")} mm`
                                    : p.heights?.length
                                      ? `${p.heights.join(", ")} mm`
                                      : null;
                              return (
                                [wh, p.surface?.trim() || null, p.color?.trim() || null, p.prelis?.trim() || null]
                                  .filter(Boolean)
                                  .join(" · ") || "—"
                              );
                            })()}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {editingProductId !== (p.id || idx) ? (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingProductId((p.id || String(idx)) as string);
                                  setEditingProductDraft({ ...p, id: p.id || crypto.randomUUID() } as DealProductItem);
                                }}
                                className="p-1.5 rounded-md border border-border bg-background hover:bg-muted text-muted-foreground hover:text-foreground"
                                title={t("deal.editLabel")}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!window.confirm(`Törölni szeretnéd a(z) ${findProductTypeForItem(productTypes, p)?.name ?? p.productType} ${p.index} terméket?`)) return;
                                  const nextProducts = measurementProducts.filter((pr: any, i: number) => (p.id && pr.id ? pr.id !== p.id : i !== idx));
                                  save.mutate({ measurementData: { products: nextProducts, gates: measurementGates } });
                                }}
                                className="p-1.5 rounded-md border border-border bg-background hover:bg-destructive/15 text-muted-foreground hover:text-destructive"
                                title={t("deal.deleteLabel")}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          ) : null}
                        </div>
                      </div>
                      {editingProductId === (p.id || String(idx)) && editingProductDraft ? (
                        <div className="mt-2">
                          {(() => {
                            const pt = findProductTypeForItem(productTypes, p);
                            if (!pt) return <p className="text-xs text-muted-foreground">{t("deal.unknownProductType")}</p>;
                            return (
                              <>
                                <ProductItemForm
                                  productType={pt}
                                  item={editingProductDraft}
                                  itemLabel={`${findProductTypeForItem(productTypes, p)?.name ?? p.productType} ${p.index}`}
                                  onChange={setEditingProductDraft}
                                />
                                <div className="flex gap-2 mt-3">
                                  <button
                                    type="button"
                                    onClick={() => { setEditingProductId(null); setEditingProductDraft(null); }}
                                    className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm"
                                  >
                                    {t("common.cancel")}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const nextProducts = measurementProducts.map((pr: any, i: number) =>
                                        i === idx ? editingProductDraft : pr
                                      );
                                      save.mutate(
                                        { measurementData: { products: nextProducts, gates: measurementGates } },
                                        { onSuccess: () => { setEditingProductId(null); setEditingProductDraft(null); } }
                                      );
                                    }}
                                    disabled={save.isPending}
                                    className="px-3 py-1.5 rounded-lg border border-primary bg-primary text-primary-foreground text-sm hover:opacity-90"
                                  >
                                    {t("common.save")}
                                  </button>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      ) : (
                        (() => {
                          const pt = findProductTypeForItem(productTypes, p);
                          const fields = pt?.measurementFields ?? pt?.measurement_fields ?? [];
                          const getValue = (field: { key: string; type?: string }) => {
                            if (field.key === "widths") return p.widths?.length ? p.widths.join(", ") : "—";
                            if (field.key === "heights") return p.heights?.length ? p.heights.join(", ") : "—";
                            if (field.key === "notes") return p.notes?.trim() || "—";
                            const v = (p as Record<string, unknown>)[field.key];
                            if (field.type === "boolean") {
                              if (v === true || v === "true") return t("common.yes");
                              if (v === false || v === "false") return t("common.no");
                              return "—";
                            }
                            return v !== undefined && v !== null && String(v).trim() !== "" ? String(v) : "—";
                          };
                          return (
                            <div className="space-y-2">
                              {fields.length === 0 ? (
                                <span className="text-muted-foreground">{t("deal.noFieldsConfigured")}</span>
                              ) : (
                                fields.map((field) =>
                                  field.key === "notes" ? (
                                    <div key={field.key} className="block border-t border-border pt-3 mt-1">
                                      <span className="text-muted-foreground block mb-1 text-sm">
                                        {getMeasurementFieldLabel(field, lang)}
                                      </span>
                                      <div className="text-left whitespace-pre-wrap font-medium text-foreground">
                                        {getValue(field)}
                                      </div>
                                    </div>
                                  ) : (
                                    <div key={field.key} className="flex justify-between gap-3 items-start">
                                      <span className="text-muted-foreground shrink-0">
                                        {getMeasurementFieldLabel(field, lang)}
                                      </span>
                                      <span className="text-right font-medium text-foreground flex-1 min-w-0 break-words">
                                        {getValue(field)}
                                      </span>
                                    </div>
                                  )
                                )
                              )}
                            </div>
                          );
                        })()
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <>
              {/* Editable Field Component (ha nincs terméklista); flat width/height/color/surface/prelis eltávolítva, a termék adatokban vannak */}
              {(
                [
                  { key: "closingDate", label: t("crm.colClosingDate"), type: "date" as const, value: d.closingDate ? d.closingDate.split("T")[0] : "" },
                ] as { key: string; label: string; type: "select" | "date"; value: string; options?: string[] }[]
              ).map((field) => (
                <div key={field.key} className="group flex items-center gap-3 py-1.5">
                  <label className="text-xs text-foreground/70 min-w-[100px]">
                    {field.label}
                  </label>
                  {editingField === field.key ? (
                    <>
                      {field.type === "select" ? (
                        <select
                          value={fieldValues[field.key] ?? field.value}
                          onChange={(e) =>
                            setFieldValues((prev) => ({
                              ...prev,
                              [field.key]: e.target.value,
                            }))
                          }
                          autoFocus
                          className="flex-1 px-2 py-1 rounded-lg border bg-background text-sm"
                        >
                          {field.options?.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt || "(válassz)"}
                            </option>
                          ))}
                        </select>
                      ) : field.type === "date" ? (
                        <input
                          type="date"
                          value={fieldValues[field.key] ?? field.value}
                          onChange={(e) =>
                            setFieldValues((prev) => ({
                              ...prev,
                              [field.key]: e.target.value,
                            }))
                          }
                          autoFocus
                          className="flex-1 px-2 py-1 rounded-lg border bg-background text-sm"
                        />
                      ) : (
                        <input
                          type="text"
                          value={fieldValues[field.key] ?? field.value}
                          onChange={(e) =>
                            setFieldValues((prev) => ({
                              ...prev,
                              [field.key]: e.target.value,
                            }))
                          }
                          autoFocus
                          className="flex-1 px-2 py-1 rounded-lg border bg-background text-sm"
                        />
                      )}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            const value = fieldValues[field.key] ?? field.value;
                            if (field.key === "closingDate") {
                              save.mutate({ closingDate: value || null });
                            } else {
                              save.mutate({ [field.key]: value });
                            }
                            setEditingField(null);
                          }}
                          className="p-1 hover:bg-green-50 dark:hover:bg-green-950/30 text-green-600 dark:text-green-400 rounded"
                          aria-label={t("common.save")}
                          type="button"
                          disabled={save.isPending}
                        >
                          <CheckIcon className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingField(null);
                            setFieldValues((prev) => ({
                              ...prev,
                              [field.key]: field.value,
                            }));
                          }}
                          className="p-1 hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600 dark:text-red-400 rounded"
                          aria-label={t("common.cancel")}
                          type="button"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 text-sm">
                        {field.value || "—"}
                      </div>
                      <button
                        onClick={() => {
                          setEditingField(field.key);
                          setFieldValues((prev) => ({
                            ...prev,
                            [field.key]: field.value,
                          }));
                        }}
                        className="p-1 opacity-0 group-hover:opacity-100 hover:bg-muted rounded transition-opacity"
                        aria-label={t("deal.edit")}
                        type="button"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              ))}
              {measurementGates.length > 1 && (
                <div className="pt-3 border-t">
                  <div className="text-xs font-semibold text-foreground/70 mb-2">{t("deal.gateData")}</div>
                  <div className="space-y-3">
                    {measurementGates.slice(1).map((gate: any, idx: number) => (
                      <div key={gate.id || idx} className="rounded-lg border bg-muted/30 p-3">
                        <div className="text-xs font-semibold mb-2">Kapu {idx + 2}</div>
                        <div className="space-y-1 text-sm">
                          <div><span className="text-xs text-foreground/70">{t("gateMeasurementExtra.widthsMm")}</span> {gate.widths?.length ? gate.widths.join(", ") : "—"}</div>
                          <div><span className="text-xs text-foreground/70">{t("gateMeasurementExtra.heightsMm")}</span> {gate.heights?.length ? gate.heights.join(", ") : "—"}</div>
                          <div><span className="text-xs text-foreground/70">{t("gateMeasurementExtra.surfaceLabel")}:</span> {gate.surface || "—"}</div>
                          <div><span className="text-xs text-foreground/70">{t("gateMeasurementExtra.colorLabel")}:</span> {gate.color || "—"}</div>
                          <div><span className="text-xs text-foreground/70">{t("gateMeasurementExtra.prelisLabel")}:</span> {gate.prelis || "—"}</div>
                          <div><span className="text-xs text-foreground/70">{t("gateMeasurementExtra.notesLabel")}</span> {gate.notes?.trim() ? gate.notes : "—"}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
                </>
              )}
              <div className="pt-2 border-t">
              <div className="text-xs font-semibold text-foreground/70 mb-3 pt-2">
                {t("deal.measurementLocationLabel")}
              </div>
              <div>
                <label className="block text-xs text-foreground/70 mb-1">
                  {t("deal.addressLabel")}
                </label>
                <div className="px-3 py-2 rounded-lg border bg-muted/30 text-sm">
                  {(() => {
                    const geo = resolveDealGeo(d as any);
                    const mapsUrl = buildMeasurementMapsUrl({
                      ...(d as any),
                      address: d.address,
                    });
                    const label = d.address || (geo ? `${geo.lat}, ${geo.lng}` : null);
                    if (!label) return "—";
                    return mapsUrl ? (
                      <a className="text-primary" href={mapsUrl} target="_blank" rel="noreferrer">
                        {label}
                      </a>
                    ) : (
                      label
                    );
                  })()}
                </div>
                {d.address &&
                  resolveDealGeo(d as any) &&
                  !isPreciseMeasurementAddress(d.address) && (
                    <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                      {t("deal.coarseMeasurementAddressHint")}
                    </p>
                  )}
              </div>
              <div>
                <label className="block text-xs text-foreground/70 mb-1 mt-2">
                  {t("deal.coordinates")}
                </label>
                <div className="px-3 py-2 rounded-lg border bg-muted/30 text-sm">
                  {(() => {
                    const geo = resolveDealGeo(d as any);
                    if (!geo) return "—";
                    return (
                      <a
                        className="text-primary"
                        href={buildMeasurementMapsUrl({ geoLat: geo.lat, geoLng: geo.lng }) || "#"}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {geo.lat}, {geo.lng}
                      </a>
                    );
                  })()}
                </div>
              </div>
            </div>
            {/* Last modified — avatar (user who edited, or TPT logo if system) + dátum */}
            {(d.updatedAt || d.updated_at || d.createdAt || d.created_at) && (
              <div className="pt-2 border-t space-y-2">
                <div className="flex items-center gap-3">
                  {(d.updatedBy ?? (d as any).updated_by) ? (
                    <Avatar className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/30">
                      <AvatarImage src={lastModifierPicture ?? undefined} className="object-cover" />
                      <AvatarFallback className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-xs font-medium">
                        {getInitialsFromEmail((d.updatedBy ?? (d as any).updated_by) as string)}
                      </AvatarFallback>
                    </Avatar>
                  ) : (
                    <div className="h-8 w-8 rounded-full overflow-hidden bg-muted flex items-center justify-center flex-shrink-0">
                      <img src="/tpt-brany-logo.png" alt={t("common.brandName")} className="h-5 w-5 object-contain" />
                    </div>
                  )}
                  <div className="text-sm">
                    <span className="text-muted-foreground">{t("deal.lastModifiedOn")} </span>
                    <span className="font-medium text-foreground">
                      {(() => {
                        const ts = d.updatedAt || d.updated_at || d.createdAt || d.created_at;
                        if (!ts) return "—";
                        const date = new Date(ts);
                        const now = new Date();
                        const isToday = date.toDateString() === now.toDateString();
                        const time = format(date, "HH:mm");
                        return isToday ? `${t("deal.today")}, ${time}` : format(date, "dd.MM.yyyy, HH:mm", { locale: dateLocale });
                      })()}
                    </span>
                  </div>
                </div>
                {(d.createdAt || d.created_at) && (
                  <div className="text-sm text-muted-foreground pl-11">
                    <span>{t("deal.createdOn")} </span>
                    <span className="font-medium text-foreground">
                      {(() => {
                        const ts = d.createdAt || d.created_at;
                        if (!ts) return "—";
                        const date = new Date(ts);
                        const now = new Date();
                        const isToday = date.toDateString() === now.toDateString();
                        const time = format(date, "HH:mm");
                        return isToday ? `${t("deal.today")}, ${time}` : format(date, "dd.MM.yyyy, HH:mm", { locale: dateLocale });
                      })()}
                    </span>
                  </div>
                )}
              </div>
            )}
            {(d.custom?.createdDate || d.custom?.city) && (
              <>
                {d.custom?.createdDate && (
                  <div>
                    <label className="block text-xs text-foreground/70 mb-1">
                      {t("crm.sortByCreatedAt")}
                    </label>
                    <div className="px-3 py-2 rounded-lg border bg-muted/30 text-sm">
                      {new Date(d.custom.createdDate).toLocaleString() || "—"}
                    </div>
                  </div>
                )}
                {d.custom?.city && (
                  <div>
                    <label className="block text-xs text-foreground/70 mb-1">
                      Deal Város
                    </label>
                    <div className="px-3 py-2 rounded-lg border bg-muted/30 text-sm">
                      {d.custom.city || "—"}
                    </div>
                  </div>
                )}
              </>
            )}
            </div>
          </div>
          </div>
        )}
        <div className={cn("w-full min-w-0", !isMobile && "self-start", isMobile && "overflow-x-hidden max-w-full")}>
          <Tabs value={activeDealTab} onValueChange={(value) => setDealTabWithDirection(value as DealTabValue)} className="w-full min-w-0">
            <AnimatePresence mode="wait" initial={false} custom={tabSwipeDirection}>
              <motion.div
                key={`${isMobile ? "mobile" : "desktop"}-${activeDealTab}`}
                custom={tabSwipeDirection}
                initial={isMobile ? { opacity: 0, x: tabSwipeDirection === 0 ? 0 : tabSwipeDirection > 0 ? 34 : -34 } : false}
                animate={isMobile ? { opacity: 1, x: 0 } : { opacity: 1, x: 0 }}
                exit={isMobile ? { opacity: 0, x: tabSwipeDirection === 0 ? 0 : tabSwipeDirection > 0 ? -28 : 28 } : { opacity: 1, x: 0 }}
                transition={{ duration: isMobile ? 0.36 : 0, ease: "easeOut" }}
              >
            {isMobile ? (
              <TabsList className="h-8 w-full min-w-0 max-w-full justify-start gap-0 p-1 text-muted-foreground">
                <TabsTrigger
                  value="information"
                  className="min-w-fit shrink-0 px-2 text-xs whitespace-nowrap"
                >
                  {t("deal.tabInformation")}
                </TabsTrigger>
                <TabsTrigger value="notes" className="min-w-fit shrink-0 px-2 text-xs whitespace-nowrap flex items-center gap-1.5" data-deal-tab="notes">
                  {t("deal.tabNotes")}
                  {notesCount > 0 && <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px] font-medium">{notesCount}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="superfaktura" className="min-w-fit shrink-0 px-2 text-xs whitespace-nowrap flex items-center gap-1.5">
                  {t("deal.tabSuperfaktura")}
                  {superfakturaCount > 0 && <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px] font-medium">{superfakturaCount}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="emails" className="min-w-fit shrink-0 px-2 text-xs whitespace-nowrap flex items-center gap-1.5">
                  {t("deal.tabEmails")}
                  {emailsCount > 0 && <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px] font-medium">{emailsCount}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="files" className="min-w-fit shrink-0 px-2 text-xs whitespace-nowrap flex items-center gap-1.5" data-deal-tab="files">
                  {t("deal.tabFiles")}
                  {filesCount > 0 && <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px] font-medium">{filesCount}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="photos" className="min-w-fit shrink-0 px-2 text-xs whitespace-nowrap flex items-center gap-1.5">
                  {t("deal.tabPhotos")}
                  {photosCount > 0 && <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px] font-medium">{photosCount}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="timeline" className="min-w-fit shrink-0 px-2 text-xs whitespace-nowrap flex items-center gap-1.5">
                  {t("deal.tabTimeline")}
                  {historyCount > 0 && <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px] font-medium">{historyCount}</Badge>}
                </TabsTrigger>
              </TabsList>
            ) : (
              <TabsList>
                <TabsTrigger value="notes" className="whitespace-nowrap flex items-center gap-1.5" data-deal-tab="notes">
                  {t("deal.tabNotes")}
                  {notesCount > 0 && <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px] font-medium">{notesCount}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="superfaktura" className="whitespace-nowrap flex items-center gap-1.5">
                  {t("deal.tabSuperfaktura")}
                  {superfakturaCount > 0 && <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px] font-medium">{superfakturaCount}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="emails" className="whitespace-nowrap flex items-center gap-1.5">
                  {t("deal.tabEmails")}
                  {emailsCount > 0 && <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px] font-medium">{emailsCount}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="files" className="whitespace-nowrap flex items-center gap-1.5" data-deal-tab="files">
                  {t("deal.tabFiles")}
                  {filesCount > 0 && <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px] font-medium">{filesCount}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="photos" className="whitespace-nowrap flex items-center gap-1.5">
                  {t("deal.tabPhotos")}
                  {photosCount > 0 && <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px] font-medium">{photosCount}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="timeline" className="whitespace-nowrap flex items-center gap-1.5">
                  {t("deal.tabTimeline")}
                  {historyCount > 0 && <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px] font-medium">{historyCount}</Badge>}
                </TabsTrigger>
              </TabsList>
            )}
            {isMobile && (
              <TabsContent value="information" className={cn("space-y-4", isMobile && MOBILE_DEAL_TAB_MIN_HEIGHT_CLASS)}>
                {/* Tulajdonos box rejtve mobilon */}

                {/* Pipeline information — unified rows: label + value + dropdown for mobile */}
                <div className="p-4 border rounded-lg bg-card space-y-3">
                  <h3 className="font-semibold text-sm mb-3">{t("deal.pipelineInfo")}</h3>

                  {/* Tímový pipeline */}
                  {teamPipeline && (
                    <div className="flex items-center justify-between gap-2 text-sm w-full min-w-0">
                      <span className="font-medium flex-shrink-0">{t("deal.teamPipeline")}</span>
                      <div className="flex items-center gap-1 flex-shrink-0 min-w-0 justify-end">
                        <span className="truncate">{getTranslatedName(teamPipeline, lang)}</span>
                        {parentPipelines.length > 0 && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="p-1 rounded hover:bg-muted flex-shrink-0 text-muted-foreground" aria-label={t("deal.edit")}>
                              <ChevronDown className="h-4 w-4" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-0" align="end">
                            <Command>
                              <CommandInput placeholder={t("deal.search")} />
                              <CommandList>
                                <CommandEmpty>{t("deal.noResults")}</CommandEmpty>
                                <CommandGroup>
                                  {parentPipelines.map((p: any) => {
                                    const subs = subPipelinesByParent.get(p.id) || [];
                                    const firstSub = subs[0];
                                    return (
                                      <CommandItem
                                        key={p.id}
                                        value={getTranslatedName(p, lang)}
                                        onSelect={() => {
                                          if (firstSub) changePipeline.mutate(firstSub.id);
                                        }}
                                        disabled={!firstSub}
                                      >
                                        <Check className={cn("mr-2 h-4 w-4 flex-shrink-0", parentPipelineId === p.id ? "opacity-100" : "opacity-0")} />
                                        <span className="truncate">{getTranslatedName(p, lang)}</span>
                                      </CommandItem>
                                    );
                                  })}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      )}
                      </div>
                    </div>
                  )}

                  {/* Sub-pipeline — show "—" with selector when none assigned */}
                  {(subPipeline || availableSubPipelinesForSelect.length > 0) && (
                    <div className="flex items-center justify-between gap-2 text-sm w-full min-w-0">
                      <span className="font-medium flex-shrink-0">{t("deal.subPipeline")}</span>
                      <div className="flex items-center gap-1 flex-shrink-0 min-w-0 justify-end">
                        <span className="truncate">
                          {subPipeline ? getTranslatedName(subPipeline, lang) : "—"}
                        </span>
                        {availableSubPipelinesForSelect.length > 0 && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="p-1 rounded hover:bg-muted flex-shrink-0 text-muted-foreground" aria-label={t("deal.edit")}>
                              <ChevronDown className="h-4 w-4" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-0" align="end">
                            <Command>
                              <CommandInput placeholder={t("deal.search")} />
                              <CommandList>
                                <CommandEmpty>{t("deal.noResults")}</CommandEmpty>
                                <CommandGroup>
                                  {availableSubPipelinesForSelect.map((p: any) => (
                                    <CommandItem
                                      key={p.id}
                                      value={getTranslatedName(p, lang)}
                                      onSelect={() => changePipeline.mutate(p.id)}
                                    >
                                      <Check className={cn("mr-2 h-4 w-4 flex-shrink-0", subPipeline?.id === p.id ? "opacity-100" : "opacity-0")} />
                                      <span className="truncate">{getTranslatedName(p, lang)}</span>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      )}
                      </div>
                    </div>
                  )}

                  {/* Dátum uzavretia — ceruza/naptár ikonra kattintva naptár popover */}
                  <div className="flex items-center justify-between gap-2 text-sm w-full min-w-0">
                    <span className="font-medium flex-shrink-0">{t("deal.closingDate")}</span>
                    <div className="flex items-center gap-1 flex-shrink-0 min-w-0 justify-end">
                    <Popover
                      open={editClosingDate}
                      onOpenChange={(open) => {
                        setEditClosingDate(open);
                        if (open) {
                          setClosingDateValue(
                            d?.closingDate ? d.closingDate.split("T")[0] : format(new Date(), "yyyy-MM-dd"),
                          );
                        }
                      }}
                    >
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="flex items-center gap-2 truncate min-w-0 justify-end hover:bg-muted rounded px-1 py-0.5 -mx-1"
                          aria-label={t("deal.edit")}
                        >
                          <span
                            className={cn(
                              "truncate",
                              d?.closingDate ? closingDateDisplayClass(dealClosingDayIso(d)) : "text-muted-foreground",
                            )}
                          >
                            {d?.closingDate
                              ? new Date(d.closingDate).toLocaleDateString("hu-HU", { year: "numeric", month: "long", day: "numeric" })
                              : "—"}
                          </span>
                          <Pencil className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 rounded-lg shadow-lg border" align="center" side="bottom" sideOffset={6}>
                        <ClosingDatePickerContent
                          value={closingDateValue}
                          onChange={setClosingDateValue}
                          onCancel={() => {
                            setEditClosingDate(false);
                            setClosingDateValue(d?.closingDate ? d.closingDate.split("T")[0] : "");
                          }}
                          onSave={() => {
                            save.mutate(
                              { closingDate: closingDateValue || null },
                              {
                                onSuccess: () => {
                                  setEditClosingDate(false);
                                  setFieldValues((prev) => ({ ...prev, closingDate: closingDateValue }));
                                },
                                onError: (error: any) => {
                                  const errorMessage = error instanceof Error ? error.message : String(error);
                                  alert(t("deal.errorPrefix") + errorMessage);
                                },
                              }
                            );
                          }}
                          saving={save.isPending}
                          locale={dateLocale}
                          compact
                        />
                      </PopoverContent>
                    </Popover>
                    </div>
                  </div>

                  {/* Etapa — row + chevron opens stage dropdown */}
                  <div className="flex items-center justify-between gap-2 text-sm w-full min-w-0">
                    <span className="font-medium flex-shrink-0">{t("deal.stageLabel")}</span>
                    <div className="flex items-center gap-1 flex-shrink-0 min-w-0 justify-end">
                      <span className="truncate">{currentStageName}</span>
                    {mobileDealStages.length > 0 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button type="button" className="p-1 rounded hover:bg-muted flex-shrink-0 text-muted-foreground" aria-label={t("deal.edit")}>
                            <ChevronDown className="h-4 w-4" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-0" align="end">
                          <Command>
                            <CommandInput placeholder={t("deal.search")} />
                            <CommandList>
                              <CommandEmpty>{t("deal.noResults")}</CommandEmpty>
                              <CommandGroup>
                                {mobileDealStages.map((s: any) => (
                                  <CommandItem
                                    key={s.id}
                                    value={getTranslatedName(s, lang)}
                                    onSelect={() => requestStageMove(s.id)}
                                  >
                                    <Check className={cn("mr-2 h-4 w-4 flex-shrink-0", (d?.stageId || d?.stage_id) === s.id ? "opacity-100" : "opacity-0")} />
                                    <span className="truncate">{getTranslatedName(s, lang)}</span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    )}
                    </div>
                  </div>
                </div>

                {/* Related Contact */}
                <div className="p-4 border rounded-lg bg-card">
                  <h3 className="font-semibold text-sm mb-3 flex items-center gap-1.5">
                    <UserCircle className="h-4 w-4" />
                    {t("deal.relatedContactHeading")}
                  </h3>
                  <RelatedContact
                    contactId={editContact ? contactIdTemp : d?.contactId}
                    edit={editContact}
                    onContactChange={setContactIdTemp}
                    contactLinkDirty={
                      !!editContact &&
                      !!contactIdTemp &&
                      contactIdTemp !== (d?.contactId || (d as any)?.contact_id || null)
                    }
                    onSaveContactLink={async () => {
                      if (!contactIdTemp) throw new Error(t("deal.contactRequired"));
                      await save.mutateAsync({ contactId: contactIdTemp });
                      qc.invalidateQueries({ queryKey: queryKeys.crmContacts });
                    }}
                    onSaved={() => setEditContact(false)}
                    onCloseEdit={() => {
                      setEditContact(false);
                      setContactIdTemp(d?.contactId || null);
                    }}
                    headerActions={
                      !editContact ? (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setEditContact(true);
                            setContactIdTemp(d?.contactId || null);
                          }}
                          className="p-1.5 rounded-md border border-border bg-background hover:bg-muted text-muted-foreground hover:text-foreground"
                          aria-label={t("deal.edit")}
                          type="button"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!contactIdTemp) {
                                alert(t("deal.contactRequired"));
                                return;
                              }
                              save.mutate(
                                { contactId: contactIdTemp },
                                {
                                  onSuccess: () => {
                                    setEditContact(false);
                                    qc.invalidateQueries({ queryKey: queryKeys.crmContacts });
                                  },
                                  onError: (error: any) => {
                                    const errorMessage = error instanceof Error ? error.message : String(error);
                                    alert(t("deal.contactSaveError") + errorMessage);
                                  },
                                }
                              );
                            }}
                            className="p-1.5 rounded-md border border-border bg-background hover:bg-muted text-green-600 dark:text-green-400"
                            aria-label={t("common.save")}
                            type="button"
                            disabled={save.isPending}
                          >
                            <CheckIcon className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setEditContact(false);
                              setContactIdTemp(d?.contactId || null);
                            }}
                            className="p-1.5 rounded-md border border-border bg-background hover:bg-muted text-red-600 dark:text-red-400"
                            aria-label={t("common.cancel")}
                            type="button"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )
                    }
                  />
                </div>

                {/* Cégek (mobile) */}
                <div className="p-4 border rounded-lg bg-card">
                  <DealCompaniesSection dealId={id!} />
                </div>

                {/* Hozzáadott termékek és Kapu adatok (mobile) */}
                <div className="p-4 border rounded-lg bg-card space-y-4">
                  {/* Hozzáadott termékek (mobilnézetben is láthatók) */}
                  {measurementProducts.length > 0 && (
                    <div className="border-t pt-4 space-y-3">
                      <h3 className="font-semibold text-sm">{t("deal.addedProducts")}</h3>
                      {measurementProducts.map((p: any, idx: number) => {
                        const pt = findProductTypeForItem(productTypes, p);
                        const fields = pt?.measurementFields ?? pt?.measurement_fields ?? [];
                        const getValue = (field: { key: string; type?: string }) => {
                          if (field.key === "widths") return p.widths?.length ? p.widths.join(", ") : "—";
                          if (field.key === "heights") return p.heights?.length ? p.heights.join(", ") : "—";
                          if (field.key === "notes") return p.notes?.trim() || "—";
                          const v = (p as Record<string, unknown>)[field.key];
                          if (field.type === "boolean") {
                            if (v === true || v === "true") return t("common.yes");
                            if (v === false || v === "false") return t("common.no");
                            return "—";
                          }
                          return v !== undefined && v !== null && String(v).trim() !== "" ? String(v) : "—";
                        };
                        const whSub =
                          p.widths?.length && p.heights?.length
                            ? `${p.widths.join(", ")} × ${p.heights.join(", ")} mm`
                            : p.widths?.length
                              ? `${p.widths.join(", ")} mm`
                              : p.heights?.length
                                ? `${p.heights.join(", ")} mm`
                                : null;
                        const subLine =
                          [whSub, p.surface?.trim() || null, p.color?.trim() || null, p.prelis?.trim() || null]
                            .filter(Boolean)
                            .join(" · ") || "—";
                        return (
                          <div key={p.id || idx} className={cn(DEAL_DETAIL_DATA_CARD, "space-y-0")}>
                            <div className="mb-3">
                              <div className="font-semibold text-foreground">{pt?.name ?? p.productType} {p.index}</div>
                              <p className="text-xs text-muted-foreground mt-0.5">{subLine}</p>
                            </div>
                            <div className="space-y-2">
                              {fields.map((field) =>
                                field.key === "notes" ? (
                                  <div key={field.key} className="border-t border-border pt-3 mt-1">
                                    <span className="text-muted-foreground block mb-1 text-sm">
                                      {getMeasurementFieldLabel(field, lang)}
                                    </span>
                                    <div className="text-left whitespace-pre-wrap font-medium text-foreground">
                                      {getValue(field)}
                                    </div>
                                  </div>
                                ) : (
                                  <div key={field.key} className="flex justify-between gap-3 items-start">
                                    <span className="text-muted-foreground shrink-0">
                                      {getMeasurementFieldLabel(field, lang)}
                                    </span>
                                    <span className="text-right font-medium text-foreground flex-1 min-w-0 break-words">
                                      {getValue(field)}
                                    </span>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {measurementGates.length > 1 && (
                    <div className="pt-3 border-t">
                      <div className="text-xs font-semibold text-foreground/70 mb-2">{t("deal.gateData")}</div>
                      <div className="space-y-3">
                        {measurementGates.slice(1).map((gate: any, idx: number) => (
                          <div key={gate.id || idx} className="rounded-lg border bg-muted/30 p-3">
                            <div className="text-xs font-semibold mb-2">Kapu {idx + 2}</div>
                            <div className="space-y-1 text-sm">
                              <div>
                                <span className="text-xs text-foreground/70">{t("gateMeasurementExtra.widthsMm")}</span>{" "}
                                {gate.widths?.length ? gate.widths.join(", ") : "—"}
                              </div>
                              <div>
                                <span className="text-xs text-foreground/70">{t("gateMeasurementExtra.heightsMm")}</span>{" "}
                                {gate.heights?.length ? gate.heights.join(", ") : "—"}
                              </div>
                              <div>
                                <span className="text-xs text-foreground/70">{t("gateMeasurementExtra.surfaceLabel")}:</span>{" "}
                                {gate.surface || "—"}
                              </div>
                              <div>
                                <span className="text-xs text-foreground/70">{t("gateMeasurementExtra.colorLabel")}:</span>{" "}
                                {gate.color || "—"}
                              </div>
                              <div>
                                <span className="text-xs text-foreground/70">{t("gateMeasurementExtra.prelisLabel")}:</span>{" "}
                                {gate.prelis || "—"}
                              </div>
                              <div>
                                <span className="text-xs text-foreground/70">{t("gateMeasurementExtra.notesLabel")}</span>{" "}
                                {gate.notes?.trim() ? gate.notes : "—"}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {measurementGates.length > 0 && (
                    <div className="pt-3 border-t">
                      <div className="text-xs font-semibold text-foreground/70 mb-2">{t("deal.gateData")}</div>
                      <div className="space-y-3">
                        {measurementGates.map((gate: any, idx: number) => (
                          <div key={gate.id || idx} className="rounded-lg border bg-muted/30 p-3">
                            <div className="text-xs font-semibold mb-2">Kapu {idx + 1}</div>
                            <div className="space-y-1 text-sm">
                              <div>
                                <span className="text-xs text-foreground/70">{t("gateMeasurementExtra.widthsMm")}</span>{" "}
                                {gate.widths?.length ? gate.widths.join(", ") : "—"}
                              </div>
                              <div>
                                <span className="text-xs text-foreground/70">{t("gateMeasurementExtra.heightsMm")}</span>{" "}
                                {gate.heights?.length ? gate.heights.join(", ") : "—"}
                              </div>
                              <div>
                                <span className="text-xs text-foreground/70">{t("gateMeasurementExtra.surfaceLabel")}:</span>{" "}
                                {gate.surface || "—"}
                              </div>
                              <div>
                                <span className="text-xs text-foreground/70">{t("gateMeasurementExtra.colorLabel")}:</span>{" "}
                                {gate.color || "—"}
                              </div>
                              <div>
                                <span className="text-xs text-foreground/70">{t("gateMeasurementExtra.prelisLabel")}:</span>{" "}
                                {gate.prelis || "—"}
                              </div>
                              <div>
                                <span className="text-xs text-foreground/70">{t("gateMeasurementExtra.notesLabel")}</span>{" "}
                                {gate.notes?.trim() ? gate.notes : "—"}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="pt-2 border-t">
                    <div className="text-xs font-semibold text-foreground/70 mb-3 pt-2">
                      {t("deal.measurementLocationLabel")}
                    </div>
                    <div>
                      <label className="block text-xs text-foreground/70 mb-1">
                        {t("deal.addressLabel")}
                      </label>
                      <div className="px-3 py-2 rounded-lg border bg-muted/30 text-sm">
                        {(() => {
                          const geo = resolveDealGeo(d as any);
                          const mapsUrl = buildMeasurementMapsUrl({
                            ...(d as any),
                            address: d?.address,
                          });
                          const label = d?.address || (geo ? `${geo.lat}, ${geo.lng}` : null);
                          if (!label) return "—";
                          return mapsUrl ? (
                            <a className="text-primary" href={mapsUrl} target="_blank" rel="noreferrer">
                              {label}
                            </a>
                          ) : (
                            label
                          );
                        })()}
                      </div>
                      {d?.address &&
                        resolveDealGeo(d as any) &&
                        !isPreciseMeasurementAddress(d.address) && (
                          <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                            {t("deal.coarseMeasurementAddressHint")}
                          </p>
                        )}
                    </div>
                    <div>
                      <label className="block text-xs text-foreground/70 mb-1 mt-2">
                        {t("deal.coordinates")}
                      </label>
                      <div className="px-3 py-2 rounded-lg border bg-muted/30 text-sm">
                        {(() => {
                          const geo = resolveDealGeo(d as any);
                          if (!geo) return "—";
                          return (
                            <a
                              className="text-primary"
                              href={buildMeasurementMapsUrl({ geoLat: geo.lat, geoLng: geo.lng }) || "#"}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {geo.lat}, {geo.lng}
                            </a>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  {/* Last modified — avatar (user who edited, or TPT logo if system) + dátum */}
                  {(d?.updatedAt || d?.updated_at || d?.createdAt || d?.created_at) && (
                    <div className="pt-2 border-t space-y-2">
                      <div className="flex items-center gap-3">
                        {(d?.updatedBy ?? (d as any)?.updated_by) ? (
                          <Avatar className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/30">
                            <AvatarImage src={lastModifierPicture ?? undefined} className="object-cover" />
                            <AvatarFallback className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-xs font-medium">
                              {getInitialsFromEmail((d.updatedBy ?? (d as any).updated_by) as string)}
                            </AvatarFallback>
                          </Avatar>
                        ) : (
                          <div className="h-8 w-8 rounded-full overflow-hidden bg-muted flex items-center justify-center flex-shrink-0">
                            <img src="/tpt-brany-logo.png" alt={t("common.brandName")} className="h-5 w-5 object-contain" />
                          </div>
                        )}
                        <div className="text-sm">
                          <span className="text-muted-foreground">{t("deal.lastModifiedOn")} </span>
                          <span className="font-medium text-foreground">
                            {(() => {
                              const ts = d?.updatedAt || d?.updated_at || d?.createdAt || d?.created_at;
                              if (!ts) return "—";
                              const date = new Date(ts);
                              const now = new Date();
                              const isToday = date.toDateString() === now.toDateString();
                              const time = format(date, "HH:mm");
                              return isToday ? `${t("deal.today")}, ${time}` : format(date, "dd.MM.yyyy, HH:mm", { locale: dateLocale });
                            })()}
                          </span>
                        </div>
                      </div>
                      {(d?.createdAt || d?.created_at) && (
                        <div className="text-sm text-muted-foreground pl-11">
                          <span>{t("deal.createdOn")} </span>
                          <span className="font-medium text-foreground">
                            {(() => {
                              const ts = d?.createdAt || d?.created_at;
                              if (!ts) return "—";
                              const date = new Date(ts);
                              const now = new Date();
                              const isToday = date.toDateString() === now.toDateString();
                              const time = format(date, "HH:mm");
                              return isToday ? `${t("deal.today")}, ${time}` : format(date, "dd.MM.yyyy, HH:mm", { locale: dateLocale });
                            })()}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {(d?.custom?.createdDate || d?.custom?.city) && (
                    <>
                      {d.custom?.createdDate && (
                        <div>
                          <label className="block text-xs text-foreground/70 mb-1">
                            {t("crm.sortByCreatedAt")}
                          </label>
                          <div className="px-3 py-2 rounded-lg border bg-muted/30 text-sm">
                            {new Date(d.custom.createdDate).toLocaleString() || "—"}
                          </div>
                        </div>
                      )}
                      {d.custom?.city && (
                        <div>
                          <label className="block text-xs text-foreground/70 mb-1">
                            Deal Város
                          </label>
                          <div className="px-3 py-2 rounded-lg border bg-muted/30 text-sm">
                            {d.custom.city || "—"}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </TabsContent>
            )}
            <TabsContent value="notes" className={cn(isMobile && MOBILE_DEAL_TAB_MIN_HEIGHT_CLASS)}>
              <DealNotesTab
                dealId={id!}
                notes={notes.data}
                notesLoading={notes.isLoading}
                addNote={{ mutate: (body) => addNote.mutate(body), isPending: addNote.isPending }}
                dateLocale={dateLocale}
              />
            </TabsContent>
            <TabsContent value="superfaktura" className={cn("mt-4", isMobile && MOBILE_DEAL_TAB_MIN_HEIGHT_CLASS)}>
              <SuperfakturaDocumentsTab dealId={id!} dealEmail={dealEmail} anchorDocumentId={d?.anchorDocumentId ?? null} />
            </TabsContent>
            <TabsContent value="emails" className={cn("mt-2", isMobile && MOBILE_DEAL_TAB_MIN_HEIGHT_CLASS)}>
              <div className="flex flex-col min-h-0 overflow-hidden h-[calc(100vh-14rem)]">
                <EmailTab
                  key={id}
                  dealId={id!}
                  contactId={deal.data?.contactId ?? (deal.data as any)?.contact_id}
                  contactEmail={dealEmail}
                  contactName={dealContactName}
                  companySendEmail="info@tptbrany.sk"
                />
              </div>
            </TabsContent>
            <TabsContent value="files" className={cn("mt-2", isMobile && MOBILE_DEAL_TAB_MIN_HEIGHT_CLASS)}>
              <FilesTab dealId={id} measurementProducts={measurementProducts} fileInputRef={filesTabFileInputRef} />
            </TabsContent>
            <TabsContent value="photos" className={cn(isMobile && MOBILE_DEAL_TAB_MIN_HEIGHT_CLASS)}>
              <PhotosTab dealId={id} />
            </TabsContent>
            <TabsContent value="timeline" className={cn("mt-2", isMobile && MOBILE_DEAL_TAB_MIN_HEIGHT_CLASS)}>
              <DealTimeline deal={deal.data} />
            </TabsContent>
              </motion.div>
            </AnimatePresence>
          </Tabs>
        </div>
      </div>

      {/* FAB quick actions (Mobile only) */}
      {isMobile && (
        <DealFabQuickActions
          dealId={id}
          measurementProducts={measurementProducts}
          onAddNote={(body) => addNote.mutateAsync(body)}
          addNotePending={addNote.isPending}
        />
      )}

      <AlertDialog open={deleteDealDialogOpen} onOpenChange={setDeleteDealDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deal.deleteDealTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deal.deleteDealDescription", {
                title: titleValue || d?.title || t("deal.dash"),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteDealMutation.isPending}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteDealMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                deleteDealMutation.mutate();
              }}
            >
              {deleteDealMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin inline" />
                  {t("common.loading")}
                </>
              ) : (
                t("common.delete")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <InstallationDateDialog
        open={!!pendingWonStageId}
        onOpenChange={(open) => {
          if (!open) setPendingWonStageId(null);
        }}
        dealTitle={titleValue || d?.title}
        confirming={installationConfirming}
        onConfirm={async (installationDate) => {
          if (!pendingWonStageId) return;
          setInstallationConfirming(true);
          try {
            move.mutate({ stageId: pendingWonStageId, installationDate });
            setPendingWonStageId(null);
          } finally {
            setInstallationConfirming(false);
          }
        }}
      />
    </div>
  );
}
